import React, { useState, useEffect } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Box, Paper, Typography, Chip, CircularProgress, Grid,
  Button, Avatar, alpha, useTheme
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { searchMinistries, searchMinistryRecords } from '../services/api';
import { getParliamentarianPhotoUrl } from '../utils/photos';
import CopyLinkButton from './CopyLinkButton';
import ShareButton from './ShareButton';

export default function MinistryDetail() {
  const { ministryId } = useParams();
  const theme = useTheme();
  const [ministry, setMinistry] = useState(null);
  const [ministryRecords, setMinistryRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        // Fetch ministry by MID
        const ministryData = await searchMinistries(`MID:${ministryId}`, 1, 1);
        if (ministryData.hits && ministryData.hits.length > 0) {
          setMinistry(ministryData.hits[0]);

          // Fetch ministry records for this ministry (paginate if needed)
          const allRecords = [];
          let page = 1;
          let hasMore = true;
          while (hasMore) {
            const recordsData = await searchMinistryRecords(`MID:${ministryId}`, page, 100);
            if (recordsData.hits && recordsData.hits.length > 0) {
              allRecords.push(...recordsData.hits);
              hasMore = allRecords.length < recordsData.total;
              page++;
            } else {
              hasMore = false;
            }
            if (page > 5) break; // Safety limit
          }
          setMinistryRecords(allRecords);
        } else {
          setError('Ministry not found');
        }
      } catch (err) {
        console.error('Error fetching ministry:', err);
        setError('Failed to load ministry data');
      }
      setLoading(false);
    };
    fetchData();
  }, [ministryId]);

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  // Build portfolio title from record
  const getPortfolioTitle = (record) => {
    const role = record.Role || '';
    const prep = record.Prep || '';
    const entity = record.Entity || '';

    // Special roles that don't need additional context
    const specialRoles = ['Prime Minister', 'Deputy Prime Minister', 'Treasurer', 'Attorney-General',
                         'Vice-President', 'Cabinet Minister', 'Cabinet Secretary', 'Assistant Treasurer',
                         'Special Minister of State'];

    if (specialRoles.includes(role)) {
      return role;
    }

    // For Minister/Parliamentary Secretary/Assistant Minister roles, build the title
    if (entity) {
      if (prep) {
        // e.g., "Minister for Defence" or "Minister Assisting the Prime Minister"
        return `${role} ${prep} ${entity}`;
      }
      return `${role} - ${entity}`;
    }

    return role;
  };

  // Get role level for sorting (1=highest)
  const getRoleLevel = (role) => {
    if (role === 'Prime Minister') return 1;
    if (role === 'Deputy Prime Minister') return 2;
    if (role === 'Treasurer') return 3;
    if (role === 'Attorney-General') return 4;
    if (role === 'Vice-President') return 5;
    if (role === 'Special Minister of State') return 6;
    if (role === 'Cabinet Minister') return 10;
    if (role === 'Cabinet Secretary') return 11;
    if (role === 'Minister') return 20;
    if (role === 'Assistant Treasurer') return 25;
    if (role === 'Parliamentary Secretary') return 30;
    if (role === 'Assistant Minister') return 30;
    return 50;
  };

  // Get seniority badge based on roles
  const getSeniorityBadge = (roles) => {
    const hasRole = (r) => roles.some(rec => rec.Role === r);

    if (hasRole('Prime Minister')) return { label: 'Prime Minister', color: 'error' };
    if (hasRole('Deputy Prime Minister')) return { label: 'Deputy PM', color: 'warning' };
    if (hasRole('Treasurer')) return { label: 'Treasurer', color: 'info' };
    if (hasRole('Cabinet Minister')) return { label: 'Cabinet', color: 'primary' };
    if (hasRole('Minister')) return { label: 'Minister', color: 'secondary' };
    if (hasRole('Assistant Minister') || hasRole('Parliamentary Secretary')) return { label: 'Assistant', color: 'default' };
    return null;
  };

  // Group records by parliamentarian and organize portfolios
  const groupedByPerson = ministryRecords.reduce((acc, record) => {
    const phid = record.PHID;
    if (!acc[phid]) {
      acc[phid] = {
        phid,
        displayName: record.DisplayName,
        chamber: record.MPorSenator,
        records: []
      };
    }
    acc[phid].records.push(record);
    return acc;
  }, {});

  // Convert to array and sort by seniority
  const sortedMembers = Object.values(groupedByPerson).map(person => {
    // Sort this person's records by role level
    person.records.sort((a, b) => getRoleLevel(a.Role) - getRoleLevel(b.Role));
    // Calculate minimum role level for sorting
    person.minRoleLevel = Math.min(...person.records.map(r => getRoleLevel(r.Role)));
    return person;
  }).sort((a, b) => a.minRoleLevel - b.minRoleLevel);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h6" color="error">{error}</Typography>
        <Button component={RouterLink} to="/ministries" startIcon={<ArrowBackIcon />} sx={{ mt: 2 }}>
          Back to Ministries
        </Button>
      </Box>
    );
  }

  const isCurrent = !ministry.DateEnd;

  return (
    <Box>
      {/* Navigation bar */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Button component={RouterLink} to="/ministries" startIcon={<ArrowBackIcon />} sx={{ color: 'text.secondary' }}>
          Back to Ministries
        </Button>
        <CopyLinkButton size="small" />
        <ShareButton size="small" />
      </Box>

      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 3,
          background: isCurrent
            ? `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.08)} 0%, ${alpha(theme.palette.success.main, 0.02)} 100%)`
            : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
          border: '1px solid',
          borderColor: isCurrent ? alpha(theme.palette.success.main, 0.2) : alpha(theme.palette.primary.main, 0.2)
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              {isCurrent && <Chip label="Current Ministry" color="success" size="small" />}
              <Chip label={ministry.MIDOrdinal} variant="outlined" size="small" />
            </Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: isCurrent ? 'success.dark' : 'primary.main' }}>
              {ministry.Name}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
              {formatDate(ministry.DateStart)} - {formatDate(ministry.DateEnd) || 'Present'}
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Ministry Members */}
      <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
          Ministry Members ({sortedMembers.length})
        </Typography>

        <Grid container spacing={2}>
          {sortedMembers.map((person) => {
            const badge = getSeniorityBadge(person.records);
            const portfolios = person.records
              .map(r => getPortfolioTitle(r))
              .filter((title, idx, arr) => arr.indexOf(title) === idx); // Remove duplicates

            return (
              <Grid item xs={12} sm={6} md={4} key={person.phid}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    height: '100%',
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderColor: 'primary.main',
                      bgcolor: alpha(theme.palette.primary.main, 0.02)
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <Avatar
                      src={getParliamentarianPhotoUrl(person.phid)}
                      sx={{ width: 56, height: 56 }}
                    >
                      {person.displayName?.[0]}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Button
                        component={RouterLink}
                        to={`/parliamentarians/${person.phid}`}
                        sx={{
                          p: 0,
                          minWidth: 'auto',
                          textTransform: 'none',
                          fontWeight: 600,
                          fontSize: '1rem',
                          color: 'text.primary',
                          textAlign: 'left',
                          justifyContent: 'flex-start',
                          '&:hover': { color: 'ledger.accentText' }
                        }}
                      >
                        {person.displayName?.split(',')[0]}
                      </Button>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                        {badge && (
                          <Chip
                            label={badge.label}
                            size="small"
                            color={badge.color}
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        )}
                        <Chip
                          label={person.chamber}
                          size="small"
                          variant="outlined"
                          color={person.chamber === 'Senator' ? 'error' : 'primary'}
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      </Box>
                      <Box sx={{ mt: 1.5 }}>
                        {portfolios.map((portfolio, idx) => (
                          <Typography
                            key={idx}
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              fontSize: '0.8rem',
                              lineHeight: 1.4,
                              mb: 0.3,
                              '&:before': {
                                content: '"•"',
                                mr: 0.5,
                                color: 'primary.main'
                              }
                            }}
                          >
                            {portfolio}
                          </Typography>
                        ))}
                      </Box>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            );
          })}
        </Grid>

        {sortedMembers.length === 0 && (
          <Typography color="text.secondary">No ministry records found for this ministry.</Typography>
        )}
      </Paper>
    </Box>
  );
}
